import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { type Tier, type TierCapabilities, getTierCapabilities } from '@/lib/content-engine/tiers';
import { resolveOwnerId } from '@/lib/identity/resolveOwnerId';

/**
 * Workspace-tenancy Phase 4: tier is derived from the brand-kit hierarchy —
 * `organizations`/`org_members` are retired.
 *
 * - 'consumer'   — the account's active kit has no parent AND is not itself a
 *                  parent of another kit (no franchise relationship at all).
 * - 'franchise'  — the account's active kit HAS a parent (`parent_brand_kit_id`
 *                  set) — a location inheriting a corporate brand kit.
 * - 'enterprise' — the account's active kit IS a parent (referenced by another
 *                  kit's `parent_brand_kit_id`) — the corporate brand owner.
 *
 * Same rule server-side: `src/lib/content-engine/resolve-tier-server.ts`.
 */
async function resolveClientTier(): Promise<Tier> {
  const accountId = await resolveOwnerId();

  const { data: activeKit } = await supabase
    .from('brand_kits')
    .select('id, parent_brand_kit_id')
    .eq('account_id', accountId)
    .eq('is_active', true)
    .maybeSingle();

  if (!activeKit) return 'consumer';
  if (activeKit.parent_brand_kit_id) return 'franchise';

  const { count } = await supabase
    .from('brand_kits')
    .select('id', { count: 'exact', head: true })
    .eq('parent_brand_kit_id', activeKit.id);

  return count && count > 0 ? 'enterprise' : 'consumer';
}

function useTierQuery() {
  const { user } = useAuth();
  return useQuery<Tier>({
    queryKey: ['content-engine-tier', user?.id],
    queryFn: resolveClientTier,
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * React hook — resolves the signed-in user's product tier from the brand-kit
 * hierarchy. Returns 'consumer' while loading or when there is no franchise
 * relationship. Prefer `useTierState()` for anything that GATES UI on tier —
 * see `TierState` below for why bare `useTier()` flashes consumer on cold load.
 */
export function useTier(): Tier {
  const { data } = useTierQuery();
  return data ?? 'consumer';
}

export function useTierCapabilities(): TierCapabilities {
  const tier = useTier();
  return getTierCapabilities(tier);
}

export interface TierState {
  tier: Tier;
  /**
   * True until the tier query has resolved at least once. Bare `useTier()`
   * returns 'consumer' both while loading AND when the account genuinely has
   * no franchise relationship — indistinguishable, so a franchise/enterprise
   * user flashes the consumer UI on every cold load (D19,
   * docs/superpowers/specs/2026-07-17-brand-kit-settings-ia-design.md §4a).
   * Callers that GATE UI on tier (not just read it) must check `isLoading`
   * before trusting `tier`.
   */
  isLoading: boolean;
}

/**
 * Loading-safe variant of `useTier()`. Prefer this over bare `useTier()` for
 * anything that conditionally renders UI based on tier.
 */
export function useTierState(): TierState {
  const { data, isFetched } = useTierQuery();
  return { tier: data ?? 'consumer', isLoading: !isFetched };
}
